import { Injectable, Injector } from "@angular/core";
import { ToasterNotificationTypeEnum } from "../enums";
import { Observable, Subject } from "rxjs";
import { ToasterNotificationModel } from "../models";
import {
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
} from "@angular/cdk/overlay";
import { ToasterRef } from "../refs";
import { ComponentPortal, PortalInjector } from "@angular/cdk/portal";
import { ToasterComponent } from "../toaster.component";

@Injectable({
  providedIn: "root",
})
export class ToasterNotificationService {
  public pushNotification$: Observable<ToasterNotificationModel>;

  private readonly overlayRef: OverlayRef;
  private pushNotificationSource: Subject<ToasterNotificationModel> =
    new Subject<ToasterNotificationModel>();

  constructor(private overlay: Overlay, private parentInjector: Injector) {
    this.pushNotification$ = this.pushNotificationSource.asObservable();

    const positionStrategy: GlobalPositionStrategy = this.positionStrategy;
    this.overlayRef = this.overlay.create({ positionStrategy });
  }

  public pushInformationNotification(title: string, message: string): void {
    this.pushNotification(
      title,
      message,
      ToasterNotificationTypeEnum.INFORMATION
    );
  }

  public pushSuccessNotification(title: string, message: string): void {
    this.pushNotification(title, message, ToasterNotificationTypeEnum.SUCCESS);
  }

  public pushWarningNotification(title: string, message: string): void {
    this.pushNotification(title, message, ToasterNotificationTypeEnum.WARNING);
  }

  public pushErrorNotification(title: string, message: string): void {
    this.pushNotification(title, message, ToasterNotificationTypeEnum.ERROR);
  }

  private get positionStrategy(): GlobalPositionStrategy {
    return this.overlay.position().global();
  }

  private pushNotification(
    title: string,
    message: string,
    type: ToasterNotificationTypeEnum
  ): void {
    if (!this.overlayRef.hasAttached()) {
      const toasterRef: ToasterRef = new ToasterRef(this.overlayRef);
      const portalInjector: PortalInjector = this.getInjector(
        this.pushNotification$,
        toasterRef,
        this.parentInjector
      );
      const toasterPortal = new ComponentPortal(
        ToasterComponent,
        null,
        portalInjector
      );

      this.overlayRef.attach(toasterPortal);
    }

    const toasterNotification: ToasterNotificationModel = {
      id: this.id,
      title: title,
      message: message,
      type: type,
    };

    this.pushNotificationSource.next(toasterNotification);
  }

  private getInjector(
    pushNotification$: Observable<ToasterNotificationModel>,
    toasterRef: ToasterRef,
    parentInjector: Injector
  ): PortalInjector {
    const tokens = new WeakMap();
    tokens.set(Observable<ToasterNotificationModel>, pushNotification$);
    tokens.set(ToasterRef, toasterRef);

    return new PortalInjector(parentInjector, tokens);
  }

  private get id(): string {
    return window.crypto.randomUUID();
  }
}
